import { useState, useEffect } from 'react';
import { useAuth } from '../../../AuthContext';
import { fetchData } from '../../../apiService';
import LogoLoader from '../../common/LogoLoader';
import { useSessionValidation } from '../../../utils/sessionValidation';
import { Refresh, GetApp, ArrowBack } from '@mui/icons-material';
import { IconButton, Tooltip, Alert, Snackbar } from '@mui/material';
import { useHistory } from 'react-router-dom';
import { exportStyledExcel } from '../../../utils/excelExport';
import { formatDate as fmtDate } from '../../../utils/dateUtils';

const fmt     = (n) => `₹${(parseFloat(n) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtQty  = (n) => (parseFloat(n) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const statusLabel = (s) => {
  const v = parseFloat(s);
  if (v === 0) return { label: 'Pending',   bg: '#fef3c7', color: '#d97706' };
  if (v === 1) return { label: 'Partial',   bg: '#dbeafe', color: '#1d4ed8' };
  if (v === 2) return { label: 'Completed', bg: '#d1fae5', color: '#059669' };
  return           { label: 'Unknown',   bg: '#f3f4f6', color: '#6b7280' };
};

const PurchaseOrderStatusReport = () => {
  useSessionValidation();
  const { companyId, branchId, token, companyName, username } = useAuth();
  const history = useHistory();

  const fmt2Date = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };

  const today = new Date();
  const [fromDate, setFromDate] = useState(fmt2Date(new Date(today.getFullYear(), today.getMonth(), 1)));
  const [toDate, setToDate]     = useState(fmt2Date(today));
  const [data, setData]         = useState([]);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');
  const [fetched, setFetched]   = useState(false);

  const fetchReport = async () => {
    if (!fromDate || !toDate) { setError('Please select date range'); return; }
        if (new Date(fromDate) > new Date(toDate)) { setError('From Date cannot be greater than To Date'); return; }
    setLoading(true);
    try {
      const res = await fetchData('ReportApi', `/POStatusReport/${companyId}/${branchId}/${fromDate}/${toDate}`, token);
      const rows = res.response_data || res || [];
      setData(Array.isArray(rows) ? rows : []);
      setFetched(true);
    } catch (err) {
      console.error(err);
      setError('Failed to fetch purchase order status report');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (companyId && branchId && token && fromDate && toDate) {
      if (new Date(fromDate) > new Date(toDate)) {
        setData([]);
        setFetched(false);
        setError('From Date cannot be greater than To Date');
        return;
      }
      fetchReport();
    }
  }, [companyId, branchId, token, fromDate, toDate]);

  const totalQty     = data.reduce((s, r) => s + (parseFloat(r.TotalQuantity) || 0), 0);
  const totalAmount  = data.reduce((s, r) => s + (parseFloat(r.TotalAmount)   || 0), 0);
  const totalPaid    = data.reduce((s, r) => s + (parseFloat(r.PaidAmount)    || 0), 0);
  const totalBalance = data.reduce((s, r) => s + (parseFloat(r.BalanceAmount) || 0), 0);

  const handleExport = async () => {
    if (!data.length) return;
    const now = new Date();
    const generatedAt = now.toLocaleString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' });

    const colHeaders = ['#', 'PO No', 'Date', 'Supplier', 'Qty', 'Amount', 'Paid', 'Balance', 'PO Status', 'Purchase Status', 'Converted', 'Verified', 'Remarks'];

    const dataRows = data.map((r, i) => [
      i + 1,
      r.PurchaseOrderNo || '',
      fmtDate(r.PurchaseOrderDate),
      r.SupplierName || '',
      parseFloat(r.TotalQuantity) || 0,
      parseFloat(r.TotalAmount)   || 0,
      parseFloat(r.PaidAmount)    || 0,
      parseFloat(r.BalanceAmount) || 0,
      r.POStatus || '',
      r.PurchaseStatus || '',
      r.IsConverted ? 'Yes' : 'No',
      r.IsVerified  ? 'Yes' : 'No',
      r.Remarks || '',
    ]);

    const totalsRow = [
      '', '', '', 'Totals',
      totalQty, totalAmount, totalPaid, totalBalance,
      '', '', '', '', '',
    ];

    const numCols = colHeaders.length;

      await exportStyledExcel({
          reportTitle: 'PURCHASE ORDER STATUS REPORT',
          sheetName: 'PO Status Report',
          fileName: `PurchaseOrderStatusReport_${fromDate}_${toDate}.xlsx`,
          companyName,
          username,
          fromDate,
          toDate,
          headers: colHeaders,
          rows: dataRows,
            totalsRow: totalsRow,
          columnWidths: [4,14,12,20,8,12,12,12,10,10,10,20],
      });
  };

  const th  = { padding: '8px 10px', textAlign: 'left',  fontWeight: 600, fontSize: 11, whiteSpace: 'nowrap', borderBottom: '1px solid rgba(255,255,255,0.2)' };
  const td  = { padding: '7px 10px', textAlign: 'left',  fontSize: 12, whiteSpace: 'nowrap' };
  const tdR = { padding: '7px 10px', textAlign: 'right', fontSize: 12, whiteSpace: 'nowrap' };
  const tdC = { padding: '7px 10px', textAlign: 'center', fontSize: 12, whiteSpace: 'nowrap' };

  return (
    <div style={{ minHeight: '100%', background: '#f0f2f5', padding: 12, fontFamily: "'Inter', sans-serif", boxSizing: 'border-box' }}>
      <LogoLoader open={loading} message="Loading purchase order status report..." />

      <Snackbar open={!!error} autoHideDuration={5000} onClose={() => setError('')} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert severity="error" onClose={() => setError('')} sx={{ width: '100%' }}>{error}</Alert>
      </Snackbar>

      <div style={{ background: '#fff', borderRadius: 10, boxShadow: '0 4px 12px rgba(0,0,0,0.08)', border: '1px solid #e2e8f0', overflow: 'hidden' }}>

        {/* Toolbar */}
        <div style={{ display: 'flex', alignItems: 'center', padding: '6px 12px', background: 'linear-gradient(135deg, #d4500a 0%, #e8734a 100%)', gap: 6, flexWrap: 'nowrap', overflowX: 'auto', overflowY: 'hidden', scrollbarWidth: 'none' }}>
          <Tooltip title="Back" arrow>
            <IconButton onClick={() => history.push('/ReportsHub')} size="small" sx={{ border: '1px solid rgba(255,255,255,0.3)', borderRadius: '8px', height: 29, width: 29, color: '#fff', flexShrink: 0, '&:hover': { background: 'rgba(255,255,255,0.1)' } }}>
              <ArrowBack sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
          <span style={{ fontSize: 13, fontWeight: 700, color: '#fff', whiteSpace: 'nowrap', flexShrink: 0 }}>Purchase Order Status Report</span>
          <div style={{ flex: 1, minWidth: 4 }} />
          <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} style={{ height: 29, border: '1px solid rgba(255,255,255,0.3)', borderRadius: 8, padding: '0 8px', fontSize: 12, backgroundColor: 'rgba(255,255,255,0.1)', color: '#fff', colorScheme: 'dark', flexShrink: 0 }} />
          <input type="date" value={toDate} onChange={e => setToDate(e.target.value)} style={{ height: 29, border: '1px solid rgba(255,255,255,0.3)', borderRadius: 8, padding: '0 8px', fontSize: 12, backgroundColor: 'rgba(255,255,255,0.1)', color: '#fff', colorScheme: 'dark', flexShrink: 0 }} />
          <Tooltip title="Refresh" arrow>
            <IconButton onClick={fetchReport} disabled={loading} size="small" sx={{ border: '1px solid rgba(255,255,255,0.3)', borderRadius: '8px', height: 29, width: 29, color: '#fff', flexShrink: 0, '&:hover': { background: 'rgba(255,255,255,0.1)' } }}>
              <Refresh sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Export Excel" arrow>
            <IconButton onClick={handleExport} disabled={!data.length} size="small" sx={{ border: '1px solid rgba(255,255,255,0.3)', borderRadius: '8px', height: 29, width: 29, color: '#fff', flexShrink: 0, '&:hover': { background: 'rgba(255,255,255,0.1)' }, '&.Mui-disabled': { color: 'rgba(255,255,255,0.3)' } }}>
              <GetApp sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        </div>

        {/* Scrollable Table */}
        <div className="report-table-scroll" style={{ overflow: 'auto', overflowY: 'scroll', maxHeight: 'calc(100vh - 130px)' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px', minWidth: 900 }}>
            <thead style={{ position: 'sticky', top: 0, zIndex: 2 }}>
              <tr style={{ background: '#e8734a', color: 'white' }}>
                <th style={th}>#</th>
                <th style={th}>PO No</th>
                <th style={th}>Date</th>
                <th style={th}>Supplier</th>
                <th style={{ ...th, textAlign: 'right' }}>Qty</th>
                <th style={{ ...th, textAlign: 'right' }}>Amount</th>
                <th style={{ ...th, textAlign: 'right' }}>Paid</th>
                <th style={{ ...th, textAlign: 'right' }}>Balance</th>
                <th style={{ ...th, textAlign: 'center' }}>PO Status</th>
                <th style={{ ...th, textAlign: 'center' }}>Purchase Status</th>
                <th style={{ ...th, textAlign: 'center' }}>Converted</th>
                <th style={{ ...th, textAlign: 'center' }}>Verified</th>
                <th style={th}>Remarks</th>
              </tr>
            </thead>
            <tbody>
              {data.map((r, i) => {
                const st = statusLabel(r.Status);
                return (
                  <tr key={r.Id || i} style={{ background: i % 2 === 0 ? '#fff' : '#fbe9e7', borderBottom: '1px solid #f0f0f0' }}>
                    <td style={td}>{i + 1}</td>
                    <td style={{ ...td, fontWeight: 600, color: '#bf360c' }}>{r.PurchaseOrderNo || '-'}</td>
                    <td style={td}>{fmtDate(r.PurchaseOrderDate)}</td>
                    <td style={{ ...td, maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.SupplierName || '-'}</td>
                    <td style={tdR}>{fmtQty(r.TotalQuantity)}</td>
                    <td style={tdR}>{fmt(r.TotalAmount)}</td>
                    <td style={{ ...tdR, color: '#059669', fontWeight: 600 }}>{fmt(r.PaidAmount)}</td>
                    <td style={{ ...tdR, color: (parseFloat(r.BalanceAmount) || 0) > 0 ? '#dc2626' : '#374151', fontWeight: 600 }}>{fmt(r.BalanceAmount)}</td>
                    <td style={tdC}>
                      <span style={{ padding: '2px 8px', borderRadius: 10, fontSize: 10, fontWeight: 600, background: st.bg, color: st.color }}>
                        {r.POStatus}
                      </span>
                    </td>
                    <td style={tdC}>
                      <span style={{ padding: '2px 8px', borderRadius: 10, fontSize: 10, fontWeight: 600, background: st.bg, color: st.color }}>
                        {r.PurchaseStatus}
                      </span>
                    </td>
                    <td style={tdC}>
                      <span style={{ padding: '2px 8px', borderRadius: 10, fontSize: 10, fontWeight: 600, background: r.IsConverted ? '#d1fae5' : '#f3f4f6', color: r.IsConverted ? '#059669' : '#6b7280' }}>
                        {r.IsConverted ? 'Yes' : 'No'}
                      </span>
                    </td>
                    <td style={tdC}>
                      <span style={{ padding: '2px 8px', borderRadius: 10, fontSize: 10, fontWeight: 600, background: r.IsVerified ? '#d1fae5' : '#f3f4f6', color: r.IsVerified ? '#059669' : '#6b7280' }}>
                        {r.IsVerified ? 'Yes' : 'No'}
                      </span>
                    </td>
                    <td style={{ ...td, maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', color: '#6b7280' }}>{r.Remarks || '-'}</td>
                  </tr>
                );
              })}
              {data.length === 0 && (
                <tr>
                  <td colSpan={12} style={{ textAlign: 'center', padding: '24px', color: '#9ca3af', fontSize: 13 }}>
                    {fetched ? 'No purchase orders found for the selected date range' : 'Select date range to load report'}
                  </td>
                </tr>
              )}
            </tbody>
            {data.length > 0 && (
              <tfoot>
                <tr style={{ background: '#ffccbc', color: '#bf360c', fontWeight: 700 }}>
                  <td colSpan={4} style={{ ...td, textAlign: 'right', fontWeight: 700 }}>Totals</td>
                  <td style={tdR}>{fmtQty(totalQty)}</td>
                  <td style={tdR}>{fmt(totalAmount)}</td>
                  <td style={tdR}>{fmt(totalPaid)}</td>
                  <td style={tdR}>{fmt(totalBalance)}</td>
                  <td colSpan={5} style={td}></td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>{/* end scrollable */}
      </div>
    </div>
  );
};

export default PurchaseOrderStatusReport;


